import { FunctionComponent, useState } from 'react'
import Button from '~/components/Button'
import { ConfirmationPhraseField, isPhraseTyped } from '~/components/ConfirmationPhraseField'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { Modal } from '~/components/Modal'

type Props = {
  /** The archived thing's own name: read back in the sentence, and expected in the field. */
  name: string
  /** The noun the page deals in — `media`, `ad unit`, `context`. Lower case; it is used mid-sentence. */
  noun: string
  /** What restoring actually puts back, in one sentence. Callers vary it by product where it matters. */
  consequence: string
  /** Some lists restore through `mutate` and some through `mutateAsync`, so both shapes are taken —
      and `mutateAsync` resolves with the restored entity, which the modal ignores. */
  onUnarchive: () => void | Promise<unknown>
  isPending?: boolean
  /**
   * The row's own Unarchive label, which the modal's three controls extend rather than restate — so the
   * confirmation stays attached to the click that opened it in the funnel.
   */
  trackingPrefix: TrackingLabel
}

/**
 * The confirmation for bringing an archived thing back, shared by the medias, ad units and contexts lists.
 *
 * Restoring used to be Pubstack's call, and each list answered an owner's click with a "write to support"
 * modal of its own. It is the owner's call now, so the three near-identical modals collapse into this one
 * and the mailto goes away.
 *
 * What replaces the old role gate is the typed name. Unarchiving is not a quiet row-level edit — it puts
 * stacks back into serving and restores bid params mappings — and it is reached from a row in a list, where
 * the cheapest mistake is restoring the neighbour of the one meant. Typing the name is what makes the
 * viewer read which row they are on, and it is the same gate `ArchiveMediaModal` puts on the way out.
 */
export const UnarchiveConfirmationModal: FunctionComponent<Props> = ({ name, noun, consequence, onUnarchive, isPending, trackingPrefix }) => {
  const { close } = useGlobalModal()
  const [typedName, setTypedName] = useState('')
  const isNameConfirmed = isPhraseTyped(name, typedName)

  const handleUnarchive = async () => {
    // The disabled button is the affordance; not restoring is the invariant.
    if (isPending || !isNameConfirmed) {
      return
    }
    await onUnarchive()
    close()
  }

  return (
    <Modal.Content>
      <Modal.Title>Unarchive {noun}</Modal.Title>
      <Modal.Body>
        <div className={'flex flex-col gap-3'}>
          <p>
            You are about to restore <strong>{name}</strong>.
          </p>
          <p>{consequence}</p>
          <ConfirmationPhraseField phrase={name} value={typedName} onChange={setTypedName} disabled={isPending} trackingLabel={`${trackingPrefix}-type`} />
        </div>
      </Modal.Body>
      <Modal.Actions>
        <Button variant={'tertiary'} onClick={close} trackingLabel={`${trackingPrefix}-cancel`}>
          Cancel
        </Button>
        <Button
          variant={'primary'}
          iconName={isPending ? 'pubstack' : 'unarchive'}
          iconSpin={isPending}
          disabled={isPending || !isNameConfirmed}
          onClick={handleUnarchive}
          trackingLabel={`${trackingPrefix}-confirm`}
        >
          {isPending ? `Unarchiving ${noun}...` : `Unarchive ${noun}`}
        </Button>
      </Modal.Actions>
    </Modal.Content>
  )
}
